import { createRoute, Link } from '@tanstack/react-router';
import { Monitor, Globe, Heart, Apple, Laptop, CheckCircle, Copy, Check } from 'lucide-react';
import { useState, useEffect } from 'react';
import { usePreferences, applyTheme, setThemeOverride } from '../hooks/usePreferences';
import { useTranslation } from '../i18n';
import type { Locale } from '../i18n';
import { Button } from '../components/ui/Button';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { KOFI_URL, GITHUB_RELEASES_URL } from '../constants/external';
import { Route as rootRoute } from './__root';
import styles from './download-success.module.css';
import landingStyles from './index.module.css';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/download/success',
  component: DownloadSuccessPage,
});

interface LicenseResult {
  email: string;
  licenseKey: string;
}

function DownloadSuccessPage() {
  const { preferences, setPreference } = usePreferences();
  const { t, locale } = useTranslation();
  const { data: downloadLinks } = useQuery({
    queryKey: ['downloadLinks'],
    queryFn: () => apiClient.getDownloadLinks(),
    staleTime: 5 * 60 * 1000,
  });

  const [license, setLicense] = useState<LicenseResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Force Vivid theme on public pages, restore user's theme on leave
  useEffect(() => {
    setThemeOverride(true);
    applyTheme('vivid');
    return () => {
      setThemeOverride(false);
      applyTheme(preferences.theme);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch license key by order_id
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const orderId = params.get('order_id');

    if (!orderId) {
      setLoading(false);
      setError(t('download.successErrorNoOrder'));
      return;
    }

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 10;
    const delay = 2000;

    async function fetchLicense() {
      try {
        const res = await fetch(`/api/license/retrieve?order_id=${encodeURIComponent(orderId!)}`);
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) {
            setLicense(data);
            setLoading(false);
          }
          return;
        }
        if (res.status === 404 && attempts < maxAttempts) {
          attempts++;
          setTimeout(fetchLicense, delay);
          return;
        }
        if (!cancelled) {
          setError(t('download.successErrorNotFound'));
          setLoading(false);
        }
      } catch {
        if (!cancelled && attempts < maxAttempts) {
          attempts++;
          setTimeout(fetchLicense, delay);
        } else if (!cancelled) {
          setError(t('download.successErrorGeneric'));
          setLoading(false);
        }
      }
    }

    fetchLicense();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- t is unstable (new ref each render); this effect should only run on mount
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleLocale = (next: Locale) => setPreference('locale', next);

  const handleCopy = async () => {
    if (!license) return;
    try {
      await navigator.clipboard.writeText(license.licenseKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select text
    }
  };

  const platforms = [
    {
      Icon: Apple,
      title: t('download.macTitle'),
      desc: t('download.macDesc'),
      button: t('download.macButton'),
      href: downloadLinks?.mac ?? GITHUB_RELEASES_URL,
    },
    {
      Icon: Monitor,
      title: t('download.winTitle'),
      desc: t('download.winDesc'),
      button: t('download.winButton'),
      href: downloadLinks?.windows ?? GITHUB_RELEASES_URL,
    },
    {
      Icon: Laptop,
      title: t('download.linuxTitle'),
      desc: t('download.linuxDesc'),
      button: t('download.linuxButton'),
      href: downloadLinks?.linux?.deb ?? GITHUB_RELEASES_URL,
    },
  ];

  const activationSteps = [
    t('download.successStep1'),
    t('download.successStep2'),
    t('download.successStep3'),
  ];

  return (
    <div className={styles.page}>
      {/* Controls */}
      <div className={landingStyles.controls}>
        <div className={landingStyles.langPill} role="group" aria-label="Language">
          <Globe size={13} strokeWidth={1.75} className={landingStyles.langIcon} />
          {(['en', 'he'] as Locale[]).map((loc) => (
            <button
              key={loc}
              className={`${landingStyles.langOption} ${locale === loc ? landingStyles.langOptionActive : ''}`}
              onClick={() => toggleLocale(loc)}
              aria-pressed={locale === loc}
            >
              {loc.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className={styles.loadingSection}>
          <div className={styles.spinner} />
          <span className={styles.loadingText}>{t('download.successLoading')}</span>
        </div>
      )}

      {/* Error state */}
      {!loading && error && (
        <div className={styles.loadingSection}>
          <p className={styles.errorText}>{error}</p>
          <Link to="/download">
            <Button size="small">{t('download.successBackToDownload')}</Button>
          </Link>
        </div>
      )}

      {/* Success state */}
      {!loading && license && (
        <>
          <section className={styles.hero}>
            <Link to="/" className={styles.backLink}>
              <span className={styles.heroPrompt}>&gt; </span>
              SiliconVault
            </Link>
            <h1 className={styles.heroTitle}>{t('download.successTitle')}</h1>
            <p className={styles.heroTagline}>{t('download.successTagline')}</p>
          </section>

          {/* License Key Card */}
          <div className={styles.licenseCard}>
            <p className={styles.licenseLabel}>{t('download.successLicenseLabel')}</p>
            <div className={styles.licenseKeyWrapper}>
              <span className={styles.licenseKeyText}>{license.licenseKey}</span>
              <button
                className={styles.copyButton}
                onClick={handleCopy}
                aria-label={t('download.successCopy')}
                title={t('download.successCopy')}
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
            {copied && <p className={styles.copiedFeedback}>{t('download.successCopied')}</p>}
            <p className={styles.licenseEmail}>{license.email}</p>
          </div>

          {/* How to Activate */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>{t('download.successActivateTitle')}</h2>
            <div className={styles.instructions}>
              {activationSteps.map((step, i) => (
                <div key={i} className={styles.step}>
                  <span className={styles.stepNumber}>
                    <CheckCircle size={16} strokeWidth={2} />
                  </span>
                  <span className={styles.stepText}>{step}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Download Links */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>{t('download.platformsTitle')}</h2>
            <div className={styles.platformGrid}>
              {platforms.map(({ Icon, title, desc, button, href }) => (
                <div key={title} className={styles.platformCard}>
                  <span className={styles.platformIcon}>
                    <Icon size={24} strokeWidth={1.5} />
                  </span>
                  <h3 className={styles.platformName}>{title}</h3>
                  <p className={styles.platformDesc}>{desc}</p>
                  <a href={href} target="_blank" rel="noopener noreferrer" className={styles.platformLink}>
                    <Button size="small">{button}</Button>
                  </a>
                </div>
              ))}
            </div>
          </section>
        </>
      )}

      {/* Footer */}
      <footer className={styles.footer}>
        <span className={styles.footerText}>
          &copy; {new Date().getFullYear()} Asaf Saar. {t('download.footer')}
        </span>
        <span className={styles.footerSep}> | </span>
        <a
          href={KOFI_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.footerSupport}
        >
          <Heart size={10} strokeWidth={2} />
          {t('support.footerLink')}
        </a>
      </footer>
    </div>
  );
}
